import { KiteIcon, KiteProgressIndicator } from '@kite/react-kite';
import React, { useState, useEffect } from 'react';
import { useLayoutContext } from '../../../providers/LayoutProvider';
import { useSearchContext } from '../../../providers/SearchProvider';
import './SearchBar.scss';
import { navigate } from 'gatsby';
import { SmartLink } from '../../../components/SmartLink/SmartLink';
import { SearchResultsContainer } from './SearchResultsContainer';
import { FloatingMenuArrow } from '../../../components/FloatingMenuArrow/FloatingMenuArrow';
import { ReachButton } from '../../../components/ReachButton/ReachButton';

export const SearchBar = (
    { 
        inline,
        mobileView
    } : { 
        inline?: boolean;
        mobileView?: boolean;
    }) => {
    const {
        setSearchOpen,
        setSidebarExpanded,
     } = useLayoutContext();
    const [ inputFocus, setInputFocus ] = useState(false);
    const {
        searchIndex,
        loadSearch,
        handleSearch,
        searchValid,
        searchResults,
        query,
        categorizedResults,
        popularSearchTerms,
    } = useSearchContext();

    const handlePopularSearch = ( term ) => {
        setSearchOpen(false);
        if (typeof window !== `undefined`) {
            history.pushState(
                null, 
                ``, 
                window.location.pathname + `?q=` + term.toString()
            );
        }
        handleSearch(term);
    };

    const handlePopularClick = (term) => {
        if (typeof window !== `undefined`) {
            const urlBuilder = new URL(
                `/search?q=${term.toString()}`,
                window.location.origin
            );
            window.location.assign(urlBuilder);
        }
        setSidebarExpanded(false);
        setSearchOpen(false);
    }

    useEffect(() => {
        loadSearch();
    }, []);

    return (
        <div className={`c--search-bar${inline ? ` inline` : ``}  ${mobileView ? `mobile-search` : ``}`}>
            <div className="kite-container light">
                {!inline && <FloatingMenuArrow />}
                <header>
                    <form
                        onSubmit={(evt) => {
                            evt.preventDefault();
                            setSearchOpen(false);
                            setSidebarExpanded(false);
                            navigate(`/search?q=${query}`);
                        }}
                    >
                        <label>
                            <span className="sr-only">Search</span>
                            <KiteIcon
                                className="search-icon"
                                icon="ki-search"
                                size="1.25rem"
                            />
                            <input
                                placeholder="Search"
                                value={query}
                                type="search"
                                onChange={(evt) => {
                                    handleSearch(evt.target.value)
                                }}
                                onFocus={() => setInputFocus(true)}
                                onBlur={() => {
                                    if (!searchValid || searchResults.length === 0) {
                                        setInputFocus(false);
                                    }
                                }}
                            />
                            {!searchIndex && (
                                <KiteProgressIndicator
                                    className="loader"
                                    id="1"
                                    size="sm"
                                />
                            )}
                            {searchValid && categorizedResults.length > 0 && (
                                <ReachButton
                                    buttonStyle="borderless"
                                    buttonIcon={{
                                        icon: `ki-backspace`,
                                        position: `right`,
                                        iconSize: `1.75rem`
                                    }}
                                    type="button"
                                    onClick={() => {
                                        handleSearch(``);
                                        setInputFocus(false);
                                    }}
                                />
                            )}
                        </label>
                    </form>
                    {!inline && (
                        <button onClick={() => setSearchOpen(false)}>
                            <KiteIcon icon="ki-x" size="2rem" />
                        </button>
                    )}
                </header>
                {searchValid && inline && (
                    <p className="results typestack-title-5">
                        {searchResults.length === 0
                            ? `No`
                            : `${searchResults.length}`}
                        {` `}
                        Results for "{query}"
                    </p>
                )}
                <nav 
                    className={`${(inputFocus || inline) ? 
                        `search-nav__displayed` : ``}`}
                    aria-hidden={inline ? `false` : !inputFocus}
                >
                    {searchResults.length === 0 && (
                        <>
                            <h3 className="typestack-eyebrow">
                                Popular Search Terms
                            </h3>
                            <ul>
                                {popularSearchTerms.map((term) => (
                                    <li key={term}>
                                        <SmartLink
                                            onMouseDown={() => {
                                                inline ? handlePopularSearch(`${term}`) : 
                                                    handlePopularClick(term)
                                            }}
                                        >
                                            {term}
                                        </SmartLink>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {categorizedResults.length > 0 && (
                        categorizedResults.map((res, index) => (
                            <SearchResultsContainer
                                key={index}
                                node={res}
                                queryParams={query}
                                inline={inline}
                            />
                        ))
                    )}
                </nav>
            </div>
        </div>
    );
};
